import { atom } from 'jotai'

import { NodeType } from 'Automations/nodeDefinitions/types'

export type NodeSize = { width: number; height: number }

/**
 * Node expansion state.
 *
 * Expansion is ephemeral (never written to the saved presentation) and keyed by
 * `NodeType` — toggling one node expands every node of the same type. Only node
 * types whose `editorNode` opts in (by passing `expandedContent` to
 * `EditorNodeTemplate`) can be expanded; this state is agnostic to which types
 * those are.
 */
export const expandedNodeTypesAtom = atom<Set<NodeType>>(new Set<NodeType>())

export const toggleNodeTypeExpandedAtom = atom(
  null,
  (get, set, nodeType: NodeType) => {
    const current = get(expandedNodeTypesAtom)
    const next = new Set(current)

    if (next.has(nodeType)) {
      next.delete(nodeType)
    } else {
      next.add(nodeType)
    }

    set(expandedNodeTypesAtom, next)
  }
)

/**
 * Settled (non-animating) node sizes, keyed by node id. The gap-preserving
 * layout reads these *target* sizes rather than the live measured size so the
 * offsets are computed once per toggle instead of churning every animation
 * frame.
 *
 * `collapsedNodeSizes` is the node's measured size while its type is collapsed
 * (captured from the ReactFlow store). `expandedNodeSizes` is the node's natural
 * size once it first renders its expanded content (captured by
 * `EditorNodeTemplate` before the size animation pins it).
 */
export const collapsedNodeSizesAtom = atom<Record<string, NodeSize>>({})
export const expandedNodeSizesAtom = atom<Record<string, NodeSize>>({})

/**
 * Progress of the in-flight expand/collapse transition: 0 = collapsed, 1 =
 * expanded. A single framer-motion animation per toggle writes this, and both
 * the node's size lerp and the rendered-node position offset read it, so the
 * node's visual centre stays fixed at every frame.
 */
export const expandTransitionProgressAtom = atom<number>(0)
